import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { configureEcho } from '@laravel/echo-react';
import axios from 'axios';

// Make Pusher available globally for Laravel Echo
window.Pusher = Pusher;

// Add axios interceptor to log broadcasting auth requests
axios.interceptors.request.use(
  (config) => {
    if (config.url?.includes('/broadcasting/auth')) {
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    if (response.config?.url?.includes('/broadcasting/auth')) {
    }
    return response;
  },
  (error) => {
    if (error.config?.url?.includes('/broadcasting/auth')) {
    }
    return Promise.reject(error);
  }
);

let echoInstance = null;
let isConfigured = false;

/**
 * Initialize Laravel Echo with Pusher configuration
 */
export const initializeLaravelEcho = () => {
  // If instance already exists and is connected, return it instead of reconnecting
  if (echoInstance && echoInstance.connector?.pusher) {
    const pusher = echoInstance.connector.pusher;
    const state = pusher.connection.state;

    // If connected or connecting, reuse the existing instance
    if (state === 'connected' || state === 'connecting') {
      return echoInstance;
    }
  }

  // Only disconnect if we need to reinitialize due to disconnection
  if (echoInstance) {
    disconnectEcho();
  }

  // Get Pusher configuration from localStorage or environment
  const savedConfig = localStorage.getItem('pusher_config');
  let pusherConfig = null;

  if (savedConfig) {
    try {
      pusherConfig = JSON.parse(savedConfig);
    } catch (e) {
    }
  }

  // Get fresh auth token for private channels - always get the latest token
  const token = localStorage.getItem('token');
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!token) {
    return null;
  }

  // Build configuration with custom authorizer to ensure fresh token on every auth request
  const config = {
    broadcaster: 'pusher',
    key: pusherConfig?.PUSHER_APP_KEY || process.env.REACT_APP_PUSHER_KEY || '',
    cluster: pusherConfig?.PUSHER_APP_CLUSTER || process.env.REACT_APP_PUSHER_CLUSTER || 'mt1',
    forceTLS: pusherConfig?.PUSHER_USE_TLS === true || pusherConfig?.PUSHER_USE_TLS === '1' || true,
    encrypted: pusherConfig?.PUSHER_APP_ENCRYPTED === true || pusherConfig?.PUSHER_APP_ENCRYPTED === '1' || true,
    enabledTransports: ['ws', 'wss'],
    disabledTransports: [],
    disableStats: true, // Disable stats to reduce console noise
    authEndpoint: `${apiUrl}/broadcasting/auth`,
    auth: {
      headers: {
        // Always get fresh token from localStorage on every auth request
        get Authorization() {
          const currentToken = localStorage.getItem('token');
          return currentToken ? `Bearer ${currentToken}` : '';
        },
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }
  };

  // Disable Pusher logging
  if (window.Pusher) {
    window.Pusher.logToConsole = false;
  }


  if (!config.key || config.key === 'your-pusher-key' || config.key === '') {
    // Return null but don't throw error - allow app to continue without Echo
    return null;
  }
  
  try {
    echoInstance = new Echo(config);
    
    // Configure the global echo instance for @laravel/echo-react
    configureEcho(echoInstance);
    isConfigured = true;
    
    // Log connection events
    if (echoInstance.connector?.pusher) {
      const pusher = echoInstance.connector.pusher;

      pusher.connection.bind('connected', () => {
      });

      pusher.connection.bind('disconnected', () => {
      });

      pusher.connection.bind('error', (error) => {
      });

      pusher.connection.bind('state_change', (states) => {
      });
    }
    
    return echoInstance;
  } catch (error) {
    return null;
  }
};

/**
 * Get the current Echo instance
 */
export const getEchoInstance = () => {
  if (!isConfigured) {
    return initializeLaravelEcho();
  }
  return echoInstance;
};

/**
 * Disconnect and cleanup Echo
 */
export const disconnectEcho = () => {
  if (echoInstance) {

    // Leave all subscribed channels first
    if (echoInstance.connector?.channels) {
      const channels = Object.keys(echoInstance.connector.channels);
      channels.forEach(channel => {
        echoInstance.leave(channel);
      });
    }

    // Disconnect the instance
    echoInstance.disconnect();

    // Clear the instance and configuration flag
    echoInstance = null;
    isConfigured = false;
  }
};

/**
 * Reinitialize Echo (useful after config changes)
 */
export const reinitializeEcho = () => {
  disconnectEcho();
  return initializeLaravelEcho();
};

/**
 * Check if a channel is subscribed
 */
export const isChannelSubscribed = (channelName) => {
  if (!echoInstance) return false;
  
  const channels = echoInstance.connector?.channels;
  if (!channels) return false;
  
  // Check if channel exists in the channels object
  return Object.prototype.hasOwnProperty.call(channels, channelName);
};

/**
 * Wait for Pusher connection to be established
 */
export const waitForConnection = async (timeout = 10000) => {
  const echo = getEchoInstance();
  if (!echo || !echo.connector?.pusher) {
    
    return false;
  }

  const pusher = echo.connector.pusher;
  const currentState = pusher.connection.state;

  // If already connected, return immediately
  if (currentState === 'connected') {
    return true;
  }

  // Wait for connection
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pusher.connection.unbind('connected', onConnected);
      pusher.connection.unbind('failed', onFailed);
      pusher.connection.unbind('unavailable', onUnavailable);
      resolve(false);
    }, timeout);

    const onConnected = () => {
      clearTimeout(timer);
      pusher.connection.unbind('connected', onConnected);
      pusher.connection.unbind('failed', onFailed);
      pusher.connection.unbind('unavailable', onUnavailable);
      resolve(true);
    };

    const onFailed = () => {
      clearTimeout(timer);
      pusher.connection.unbind('connected', onConnected);
      pusher.connection.unbind('failed', onFailed);
      pusher.connection.unbind('unavailable', onUnavailable);
      resolve(false);
    };

    const onUnavailable = () => {
      clearTimeout(timer);
      pusher.connection.unbind('connected', onConnected);
      pusher.connection.unbind('failed', onFailed);
      pusher.connection.unbind('unavailable', onUnavailable);
      resolve(false);
    };

    pusher.connection.bind('connected', onConnected);
    pusher.connection.bind('failed', onFailed);
    pusher.connection.bind('unavailable', onUnavailable);

    // Log state changes for debugging
    pusher.connection.bind('state_change', (states) => {
    });
  });
};

/**
 * Wait for channel subscription with timeout
 */
export const waitForChannelSubscription = async (channelName, timeout = 5000) => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    if (isChannelSubscribed(channelName)) {
      return true;
    }

    // Wait a bit before checking again
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  return false;
};

/**
 * Ensure user channel is subscribed
 */
export const ensureUserChannelSubscribed = async (userId) => {
  if (!userId) {
    return false;
  }

  // For private channels, Laravel Echo expects the channel name without 'private-' prefix
  // It will automatically add it when subscribing
  const channelName = `App.User.${userId}`;
  const fullChannelName = `private-${channelName}`;

  // Check if already subscribed (check with full name)
  if (isChannelSubscribed(fullChannelName)) {
    return true;
  }

  // Try to subscribe
  const echo = getEchoInstance();
  if (!echo) {
    return false;
  }

  const connected = await waitForConnection();
  if (!connected) {
    return false;
  }

  // Get current socket_id before subscribing
  const socketId = echo.connector?.pusher?.connection?.socket_id;
  const authEndpoint = `${process.env.REACT_APP_API_URL}/broadcasting/auth`;
  // Subscribe as private channel (Echo will add 'private-' prefix)
  // This will trigger Pusher to automatically call /broadcasting/auth with proper socket_id
  const channel = echo.private(channelName);

  // Add event listeners for debugging
  channel.listen('.pusher:subscription_succeeded', () => {
  });

  channel.error((error) => {
  });

  // Wait for subscription (check with full name)
  const subscribed = await waitForChannelSubscription(fullChannelName);

  if (subscribed) {
  } else {
  }

  return subscribed;
};

export default {
  initialize: initializeLaravelEcho,
  getInstance: getEchoInstance,
  disconnect: disconnectEcho,
  reinitialize: reinitializeEcho,
  isChannelSubscribed,
  waitForConnection,
  waitForChannelSubscription,
  ensureUserChannelSubscribed
};